import React, { useMemo } from "react";

import { type DocsDataTabGrouped } from "@hsl/core/documents/types";
import cx from "@hsl/core/utils/cx";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import useIsEnglishLang from "../../../hooks/useIsEnglishLang";
import { Translate } from "../../../hooks/useTranslation";
import { keyDocType } from "../../../lib/kidOrKiid";
import { generateLocalId } from "../../../store/helpers";
import useExplorerStore from "../../../store/store";
import { TextSkeleton } from "../../Skeletons";
import Tabs from "../../Tabs";
import { useDocumentContext } from "../context";
import { groupByDocType } from "../helpers/groupByDocType";
import { DocumentDisplayListWithCheckbox } from "./DocumentDisplay";
import FactSheetAndKiids, { EFTFactSheetAndKiids } from "./FactSheetAndKiids";
import FactSheetOrKIIDModalTabContent from "./FactSheetOrKIIDModalTabContent";
import { renderFundDoc } from "./FundDoc";
import KeyDocumentsHeader from "./KeyDocumentsHeader";
import TextButton from "./TextButton";

interface Props {
    fund: FundDataWithType;
    className?: string;
}

const KeyDocumentsSection = ({ fund, className }: Props) => {
    const jurisdiction = useExplorerStore(
        (state) => state.config.jurisdictionId,
    );
    const productType = useExplorerStore((state) => state.config.productType);
    const docType = keyDocType(jurisdiction, productType);
    const {
        docsData: { data, isLoading },
        selectionActive,
        selectedDocumentURLs,
        addMultipleDocs,
        removeMultipleDocs,
        hasFactsheet,
        hasESG,
        hasUpdates,
        markRead,
    } = useDocumentContext();
    const isEnglish = useIsEnglishLang();

    const fundLiterature: DocsDataTabGrouped | undefined = React.useMemo(() => {
        const activeSet = data && data.find(([set]) => set.rank === 1);
        if (activeSet) {
            return [activeSet[0], Object.values(groupByDocType(activeSet[1]))];
        }
    }, [data]);

    const isETF = fund.type === "etf";

    const allFundLiteratureSelected = React.useMemo(
        () =>
            fundLiterature &&
            fundLiterature[1]!.every((x) =>
                selectedDocumentURLs.some((y) =>
                    x.map((z) => z.url).includes(y.value),
                ),
            ),
        [fundLiterature, selectedDocumentURLs],
    );

    const handleSelectAll = (evt: React.MouseEvent) => {
        evt.stopPropagation();
        if (fundLiterature && fundLiterature[1]) {
            const fundLiteratureDocSet = fundLiterature[1].flatMap(
                (docSetArr) => {
                    const multipleLanguagesPresent = docSetArr.length > 1;
                    return docSetArr.flatMap((doc) => ({
                        name: `${doc.language_aware_display_name}${
                            multipleLanguagesPresent && `( ${doc.language})`
                        }`,
                        value: doc.url,
                    }));
                },
            );
            allFundLiteratureSelected
                ? removeMultipleDocs(fundLiteratureDocSet)
                : addMultipleDocs(fundLiteratureDocSet);
        }
    };

    return (
        <div
            className={cx([
                className,
                "grid grid-cols-2",
                { "xl:grid-cols-6": productType !== "pmc" },
            ])}
        >
            <div
                className={cx(
                    "border-r-2 border-gray-100 pr-4 text-sm",
                    productType !== "pmc" && "xl:col-span-2",
                )}
            >
                {isLoading ? (
                    <>
                        <KeyDocumentsHeader>
                            <TextSkeleton width={"Fund literature".length} />
                        </KeyDocumentsHeader>
                        <div className="flex flex-col gap-1 py-3">
                            <TextSkeleton width={20} />
                            <TextSkeleton width={22} />
                            <TextSkeleton width={18} />
                            <TextSkeleton width={21} />
                        </div>
                    </>
                ) : fundLiterature ? (
                    <>
                        <KeyDocumentsHeader className="flex justify-between">
                            <span>{fundLiterature[0].display_name}</span>
                            {selectionActive && (
                                <TextButton onClick={handleSelectAll}>
                                    <Translate>
                                        {allFundLiteratureSelected
                                            ? "Clear all"
                                            : "Select all"}
                                    </Translate>
                                </TextButton>
                            )}
                        </KeyDocumentsHeader>
                        <div className="py-2">
                            <DocumentDisplayListWithCheckbox
                                data={fundLiterature[1]}
                                selectionActive={selectionActive}
                                showLang={!isEnglish}
                                hasUpdates={hasUpdates}
                                markRead={markRead}
                            />
                        </div>
                    </>
                ) : null}
            </div>
            <div
                className={cx(
                    "border-r border-gray-100 px-4",
                    productType !== "pmc" && "xl:col-span-3",
                )}
            >
                {isETF ? (
                    <EFTFactSheetAndKiids
                        fund={fund.data}
                        renderHeader={(title) => `${title} ETF Class`}
                    />
                ) : (
                    <>
                        <KeyDocumentsHeader className="text-right">
                            <div className="flex w-full justify-between">
                                <span>
                                    <Translate>
                                        {fund.type !== "pmc"
                                            ? "Share class"
                                            : fund.data.data.card_variant ===
                                                "Normal PMC Fund card"
                                              ? "Iteration type"
                                              : "Fund"}
                                    </Translate>
                                </span>
                                <span>
                                    {hasFactsheet && (
                                        <>
                                            <span>
                                                <Translate>
                                                    Fact sheet
                                                </Translate>
                                            </span>{" "}
                                        </>
                                    )}
                                    <span className="ml-4">
                                        {docType.toUpperCase()}
                                    </span>
                                    {hasESG && (
                                        <span className="ml-4">
                                            <Translate>
                                                ESG <br />
                                                report
                                            </Translate>
                                        </span>
                                    )}
                                </span>
                            </div>
                        </KeyDocumentsHeader>
                        <FactSheetAndKiids
                            shareClasses={fund.data.share_classes.map(
                                generateLocalId("shareclass"),
                            )}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default KeyDocumentsSection;

export const ModalKeyDocumentsSection = ({ fund }: Props) => {
    const jurisdiction = useExplorerStore(
        (state) => state.config.jurisdictionId,
    );
    const productType = useExplorerStore((state) => state.config.productType);
    const isPMC = productType === "pmc";
    const docType = keyDocType(jurisdiction, productType);
    const {
        docsData: { data, isLoading },
        selectionActive,
    } = useDocumentContext();
    const isEnglish = useIsEnglishLang();

    const locale = useExplorerStore((state) => state.config.locale);

    const formatter = useMemo(
        () =>
            new Intl.DateTimeFormat([locale.replace("_", "-"), "en-GB"], {
                year: "numeric",
                month: "short",
                day: "numeric",
            }),
        [locale],
    );

    const fundLiterature = data && data.find(([set]) => set.set_id === 1);

    const firstTab = fundLiterature?.[0] && {
        key: `fund-literature-${fund.data.id}-${fundLiterature[0].set_id}`,
        label: fundLiterature[0].display_name,
    };
    const firstContent =
        fundLiterature?.[1] &&
        fundLiterature[1].map((doc) =>
            renderFundDoc(
                {
                    ...doc,
                    date: formatter.format(new Date(doc.date)),
                },
                selectionActive,
                !isEnglish,
            ),
        );
    const tabs = [
        {
            key: `Fact sheet`,
            label: `Fact sheet${isPMC ? " (Gross)" : ""}`,
        },
        {
            key: `Fact sheet`,
            label: `Fact sheet${isPMC ? " (Net)" : ""}`,
        },
        {
            key: `${docType.toUpperCase()}`,
            label: `${docType.toUpperCase()}`,
        },
    ];
    const nonPMCTabs = [
        isLoading
            ? {
                  key: `first-tab-${fund.data.id}`,
                  label: <TextSkeleton width={12} />,
              }
            : firstTab ?? { key: "", label: "" },
    ];
    const content = [
        <FactSheetOrKIIDModalTabContent
            type="factSheet"
            shareClasses={fund.data.share_classes.map(
                generateLocalId("shareclass"),
            )}
        />,
        <FactSheetOrKIIDModalTabContent
            type="memberCommunication"
            shareClasses={fund.data.share_classes.map(
                generateLocalId("shareclass"),
            )}
        />,
        <FactSheetOrKIIDModalTabContent
            type="kiid"
            shareClasses={fund.data.share_classes.map(
                generateLocalId("shareclass"),
            )}
        />,
    ];
    const nonPMCContent = [
        isLoading ? (
            <div className="flex flex-col gap-1 py-3">
                <TextSkeleton width={20} />
                <TextSkeleton width={22} />
                <TextSkeleton width={18} />
                <TextSkeleton width={21} />
            </div>
        ) : (
            firstContent
        ),
    ];

    return (
        <Tabs
            withBorder
            tabs={isPMC ? tabs : nonPMCTabs.concat(tabs.toSpliced(1, 1))}
            tabClassName="mr-12"
            content={
                isPMC ? content : nonPMCContent.concat(content.toSpliced(1, 1))
            }
            contentContainerClassName="pt-4 pr-4"
        />
    );
};
